import { StandardizedBalanceData, BalanceValues, BalanceTotals, balanceInfo } from '../types/interfaces';
import { toNumber } from './Utils';

/**
 * Agrega los campos detallados de patrimonio neto en Capital y Otros
 * (misma lógica que normalizeNetWorth de BalanceLoader) para la vista
 * simplificada en Comité.
 *
 * Capital = capital + capital_adjustment
 * Otros   = legal_reserves + other_reserves + irrevocable_contributions
 *         + technical_revaluation + prior_accumulated + result_of_period + other
 */
export const normalizeNetWorthForDisplay = (balance: balanceInfo): balanceInfo => {
  const config = JSON.parse(localStorage.getItem('config') ?? '{}');
  const isDetailedNetWorth = config?.isDetailedNetWorthEnabled ?? false;
  if (isDetailedNetWorth || !balance?.net_worth) return balance;

  const nw = balance.net_worth as Record<string, any>;
  const toNum = (v: any): number => {
    if (v === null || v === undefined || v === '') return 0;
    if (typeof v === 'number') return v;
    const n = parseFloat(String(v));
    return isNaN(n) ? 0 : n;
  };

  const capitalCalc = toNum(nw.capital) + toNum(nw.capital_adjustment);
  const otherCalc =
    toNum(nw.legal_reserves) +
    toNum(nw.other_reserves) +
    toNum(nw.irrevocable_contributions) +
    toNum(nw.technical_revaluation) +
    toNum(nw.prior_accumulated) +
    toNum(nw.result_of_period) +
    toNum(nw.other);

  return {
    ...balance,
    net_worth: {
      capital: String(Math.round(capitalCalc)),
      legal_reserves: '0',
      other_reserves: '0',
      capital_adjustment: '0',
      irrevocable_contributions: '0',
      technical_revaluation: '0',
      prior_accumulated: '0',
      result_of_period: '0',
      other: String(Math.round(otherCalc)),
    },
  };
};

/**
 * Convierte datos del balance sheet a la estructura estandarizada { values, totals }
 */
export const standardizeBalanceData = (balanceData: any): StandardizedBalanceData => {
  const formatEuro = (value: number) => {
    // Redondear explícitamente a 2 decimales para evitar errores de precisión
    const rounded = Math.round(value * 100) / 100;
    return new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(rounded);
  };

  // Parseo formato europeo (ej. "2.994.227,62") y redondeo a entero para totales (fix 49267)
  const sumValues = (obj: any) => {
    const sum = Object.values(obj || {}).reduce((sum: number, val: any) => sum + toNumber(val || 0), 0);
    return Math.round(sum);
  };

  const safeParseFloat = (value: any, defaultValue: number = 0) => {
    if (value === null || value === undefined || value === '') return defaultValue;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? defaultValue : parsed;
  };

  /** Parsea valor (número o string 300.56 / 300,56) y redondea al entero para Carga de balance (ej. 300.56 → 301). */
  const parseAndRoundToInteger = (value: any, defaultValue: number = 0): number => {
    if (value === null || value === undefined || value === '') return defaultValue;
    if (typeof value === 'number') return Number.isFinite(value) ? Math.round(value) : defaultValue;
    const s = String(value).trim();
    if (!s) return defaultValue;
    // Formato europeo (coma decimal): "300,56" → toNumber
    const n = s.includes(',') ? toNumber(s) : parseFloat(s);
    return Number.isFinite(n) ? Math.round(n) : defaultValue;
  };

  // Calcular totales con redondeo en cada paso para evitar acumulación de errores. toNumber parsea número o string en formato europeo.
  const gross_result = Math.round((
    toNumber(balanceData.gross_result?.agricultural_production) -
    toNumber(balanceData.gross_result?.production_cost) +
    toNumber(balanceData.gross_result?.sales) -
    toNumber(balanceData.gross_result?.cost_of_sales)
  ) * 100) / 100;

  // Resultado bruto - Gastos comercialización - Gastos administración + otros ingresos - otros egresos = Resultado operativo
  const operating_result = Math.round((
    gross_result -
    toNumber(balanceData.operating_result?.commercialization_expenses) -
    toNumber(balanceData.operating_result?.administrative_expenses) +
    toNumber(balanceData.operating_result?.other_income) -
    toNumber(balanceData.operating_result?.other_expenses)
  ) * 100) / 100;

  // Resultado operativo - gastos financieros +/- dif de cambio + resultado por tenencia = Resultado ordinario
  const ordinary_result = Math.round((
    operating_result -
    toNumber(balanceData.ordinary_result?.financial_expenses) +
    toNumber(balanceData.ordinary_result?.exchange_difference_and_holding_result)
  ) * 100) / 100;

  const result_before_taxes = Math.round((
    ordinary_result +
    toNumber(balanceData.result_before_taxes?.extraordinary_income) -
    toNumber(balanceData.result_before_taxes?.extraordinary_expenses)
  ) * 100) / 100;

  // Resultado del ejercicio = resultado antes de impuestos menos impuesto a las ganancias.
  const result_for_the_year = Math.round((
    result_before_taxes + toNumber(balanceData.taxes?.income_tax)
      ) * 100) / 100;

  const total_current =
    (balanceData.current_assets ? sumValues(balanceData.current_assets) : 0) +
    (balanceData.non_current_assets ? sumValues(balanceData.non_current_assets) : 0);
  
  const total_non_current =
    (balanceData.current_liabilities ? sumValues(balanceData.current_liabilities) : 0) +
    (balanceData.non_current_liabilities ? sumValues(balanceData.non_current_liabilities) : 0);
  
  const total_net_worth = balanceData.net_worth ? sumValues(balanceData.net_worth) : 0;
  
  const total_net_worth_and_non_current =
    (balanceData.current_liabilities ? sumValues(balanceData.current_liabilities) : 0) +
    (balanceData.non_current_liabilities ? sumValues(balanceData.non_current_liabilities) : 0) +
    (balanceData.net_worth ? sumValues(balanceData.net_worth) : 0) +
    (balanceData.non_controlling_interest_result ? sumValues(balanceData.non_controlling_interest_result) : 0);

  // Crear estructura de valores (convertir strings a numbers)
  const values: BalanceValues = {
    id: balanceData.id,
    cuit: balanceData.cuit,
    code: balanceData.code,
    from_month: balanceData.from_month,
    from_year: balanceData.from_year,
    to_month: balanceData.to_month,
    to_year: balanceData.to_year,
    current_assets: {
      current_investments: parseAndRoundToInteger(balanceData.current_assets?.current_investments),
      cash_and_cash_equivalents: parseAndRoundToInteger(balanceData.current_assets?.cash_and_cash_equivalents),
      trade_receivables_short_term: parseAndRoundToInteger(balanceData.current_assets?.trade_receivables_short_term),
      other_receivables: parseAndRoundToInteger(balanceData.current_assets?.other_receivables),
      inventory_short_term: parseAndRoundToInteger(balanceData.current_assets?.inventory_short_term),
      other_current_assets: parseAndRoundToInteger(balanceData.current_assets?.other_current_assets),
    },
    non_current_assets: {
      trade_receivables_long_term: parseAndRoundToInteger(balanceData.non_current_assets?.trade_receivables_long_term),
      other_receivables: parseAndRoundToInteger(balanceData.non_current_assets?.other_receivables),
      permanent_investments: parseAndRoundToInteger(balanceData.non_current_assets?.permanent_investments),
      property_plant_and_equipment: parseAndRoundToInteger(balanceData.non_current_assets?.property_plant_and_equipment),
      other_non_current_assets: parseAndRoundToInteger(balanceData.non_current_assets?.other_non_current_assets),
    },
    current_liabilities: {
      trade_payables_short_term: parseAndRoundToInteger(balanceData.current_liabilities?.trade_payables_short_term),
      bank_and_financial_debt_short_term: parseAndRoundToInteger(balanceData.current_liabilities?.bank_and_financial_debt_short_term),
      social_security_debt_short_term: parseAndRoundToInteger(balanceData.current_liabilities?.social_security_debt_short_term),
      tax_liabilities_short_term: parseAndRoundToInteger(balanceData.current_liabilities?.tax_liabilities_short_term),
      other_debts_provisions: parseAndRoundToInteger(balanceData.current_liabilities?.other_debts_provisions),
      projections: parseAndRoundToInteger(balanceData.current_liabilities?.projections),
      other_liabilities: parseAndRoundToInteger(balanceData.current_liabilities?.other_liabilities),
    },
    non_current_liabilities: {
      non_current_liabilities: parseAndRoundToInteger(balanceData.non_current_liabilities?.non_current_liabilities),
      trade_payables_long_term: parseAndRoundToInteger(balanceData.non_current_liabilities?.trade_payables_long_term),
      bank_and_financial_debt_long_term: parseAndRoundToInteger(balanceData.non_current_liabilities?.bank_and_financial_debt_long_term),
      social_security_debt_long_term: parseAndRoundToInteger(balanceData.non_current_liabilities?.social_security_debt_long_term),
      tax_liabilities_long_term: parseAndRoundToInteger(balanceData.non_current_liabilities?.tax_liabilities_long_term),
      other_long_term_liabilities: parseAndRoundToInteger(balanceData.non_current_liabilities?.other_long_term_liabilities),
    },
    non_controlling_interest_result: {
      non_controlling_interest_result: parseAndRoundToInteger(balanceData.non_controlling_interest_result?.non_controlling_interest_result),
    },
    net_worth: (() => {
      const netWorth = balanceData.net_worth || {};
      const result: any = {
        capital: parseAndRoundToInteger(netWorth.capital),
        legal_reserves: parseAndRoundToInteger(netWorth.legal_reserves),
        other_reserves: parseAndRoundToInteger(netWorth.other_reserves),
        capital_adjustment: parseAndRoundToInteger(netWorth.capital_adjustment),
        irrevocable_contributions: parseAndRoundToInteger(netWorth.irrevocable_contributions),
        technical_revaluation: parseAndRoundToInteger(netWorth.technical_revaluation),
        prior_accumulated: parseAndRoundToInteger(netWorth.prior_accumulated),
        result_of_period: parseAndRoundToInteger(netWorth.result_of_period),
        other: parseAndRoundToInteger(netWorth.other),
      };

      // Si existe other_components (legacy) y other está vacío, migrar
      const otherComponents = parseAndRoundToInteger(netWorth.other_components);
      if (otherComponents !== 0 && result.other === 0) {
        result.other = otherComponents;
      }

      return result;
    })(),
    gross_result: {
      agricultural_production: parseAndRoundToInteger(balanceData.gross_result?.agricultural_production),
      production_cost: parseAndRoundToInteger(balanceData.gross_result?.production_cost),
      sales: parseAndRoundToInteger(balanceData.gross_result?.sales),
      cost_of_sales: parseAndRoundToInteger(balanceData.gross_result?.cost_of_sales),
    },
    operating_result: {
      commercialization_expenses: parseAndRoundToInteger(balanceData.operating_result?.commercialization_expenses),
      administrative_expenses: parseAndRoundToInteger(balanceData.operating_result?.administrative_expenses),
      other_income: parseAndRoundToInteger(balanceData.operating_result?.other_income),
      other_expenses: parseAndRoundToInteger(balanceData.operating_result?.other_expenses),
    },
    ordinary_result: {
      financial_expenses: parseAndRoundToInteger(balanceData.ordinary_result?.financial_expenses),
      exchange_difference_and_holding_result: parseAndRoundToInteger(balanceData.ordinary_result?.exchange_difference_and_holding_result),
    },
    result_before_taxes: {
      extraordinary_income: parseAndRoundToInteger(balanceData.result_before_taxes?.extraordinary_income),
      extraordinary_expenses: parseAndRoundToInteger(balanceData.result_before_taxes?.extraordinary_expenses),
    },
    taxes: {
      income_tax: parseAndRoundToInteger(balanceData.taxes?.income_tax),
    },
    ...(balanceData.purchases != null ? { purchases: safeParseFloat(balanceData.purchases) } : {}),
    ...(balanceData.amortization_depreciation != null ? { amortization_depreciation: safeParseFloat(balanceData.amortization_depreciation) } : {}),
  };

  // Crear estructura de totales (formateados como strings)
  const totals: BalanceTotals = {
    total_current: formatEuro(total_current),
    total_non_current: formatEuro(total_non_current),
    total_net_worth: formatEuro(total_net_worth),
    total_net_worth_and_non_current: formatEuro(total_net_worth_and_non_current),
    gross_result: formatEuro(gross_result),
    operating_result: formatEuro(operating_result),
    ordinary_result: formatEuro(ordinary_result),
    result_before_taxes: {
      extraordinary_income: formatEuro(toNumber(balanceData.result_before_taxes?.extraordinary_income)),
      extraordinary_expenses: formatEuro(toNumber(balanceData.result_before_taxes?.extraordinary_expenses)),
      total: result_before_taxes,
      totalFormatted: formatEuro(result_before_taxes),
    },
    result_before_taxes_calculated: result_before_taxes,
    taxes: formatEuro(result_for_the_year),
    income_tax_formatted: formatEuro(toNumber(balanceData.taxes?.income_tax)),
  };

  return {
    values,
    totals
  };
};
